import React, { lazy, Suspense, useEffect, useState } from "react";
import axios from "axios";
import { relativeRoute } from "@/helpers/relativeRoute";
import { columnsLabel, money } from "@/helpers/landingFormat";
import { LandingProps } from "@/types/Landing";
import styles from "./LandingPaperDetails.module.scss";

// leaflet needs the browser, keep it out of the server side render
const Map = lazy(() => import("@/Molecules/Map"));

type Facts = Pick<LandingProps, 'priceFrom' | 'schedule' | 'format'>;

/**
 * Price, deadlines and distribution area of a paper, loaded when the paper is expanded in the request form
 */
const LandingPaperDetails = ({slug}: { slug: string }) => {
  const [facts, setFacts] = useState<Facts | null>(null);
  const [failed, setFailed] = useState(false);
  // undefined: loading, null: no distribution area
  const [geometry, setGeometry] = useState<object | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    axios.get(relativeRoute('seo.obituary.facts', {slug}))
      .then((response) => !cancelled && setFacts(response.data))
      .catch(() => !cancelled && setFailed(true));
    axios.get(relativeRoute('seo.obituary.distribution', {slug}))
      .then((response) => !cancelled && setGeometry(response.data || null))
      .catch(() => !cancelled && setGeometry(null));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (failed) {
    return <p className={styles.details}>De gegevens van deze krant konden niet worden geladen.</p>;
  }
  if (!facts) {
    return <p className={styles.details}>Bezig met laden...</p>;
  }

  const {priceFrom, schedule, format} = facts;

  return <div className={styles.details}>
    <div>
      <h4>Tarief</h4>
      {priceFrom
        ? <p>
          Vanaf <strong>€ {money(priceFrom.price)}</strong> incl. btw voor {columnsLabel(priceFrom.columns)}
          {priceFrom.width && <> ({priceFrom.width} mm breed)</>} × {priceFrom.height} mm in zwart-wit.
        </p>
        : <p>De prijs ontvang je bij het ontwerp.</p>}
      {format.color && <p>{format.color}.</p>}
    </div>
    <div>
      <h4>Deadlines</h4>
      <table>
        <thead>
        <tr>
          <th>Plaatsing</th>
          <th>Aanleveren vóór</th>
        </tr>
        </thead>
        <tbody>
        {schedule.map((row) => <tr key={row.day}>
          <td className={'capitalize'}>{row.day}</td>
          <td>{row.deadlineDay} {row.deadlineTime}</td>
        </tr>)}
        </tbody>
      </table>
    </div>
    <div>
      <h4>Verspreidingsgebied</h4>
      {geometry === undefined && <p>Bezig met laden...</p>}
      {geometry === null && <p>Van deze krant is geen verspreidingsgebied bekend.</p>}
      {geometry && <div className={styles.map}>
        <Suspense fallback={null}><Map geoJSON={geometry} mapKey={slug} /></Suspense>
      </div>}
    </div>
  </div>;
};

export default LandingPaperDetails;
